import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import {
  Alert,
  Box,
  Button,
  Heading,
  HelperText,
  Label,
  Stack,
  TextInput,
  TextInputControl,
  TextInputSlot,
} from "@deliveryhero/cape-core";
import { VisibilityOffIcon, VisibilityOnIcon } from "@deliveryhero/cape-icons";
import { useState } from "react";
import {
  Form,
  Link,
  redirect,
  useActionData,
  useLoaderData,
  useNavigation,
  useRouteLoaderData,
} from "react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { env } from "~/config";
import type { loader as rootLoader } from "~/root";
import { BRAND_DISPLAY_NAMES } from "~/lib/brand";
import { i18n } from "~/lib/i18n";
import { getBrandName } from "~/lib/meta";
import { decodeJWT, makeFixtureJWT, setSessionHeaders } from "~/lib/session";
import { AUTH_FIXTURES } from "~/mocks/fixtures/auth.fixtures";
import type { Route } from "./+types/route";
import LoginSvg from "./login.svg?react";
import * as styles from "./login.css";

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const BLOCKED_DOMAINS = ["gmail.com", "yahoo.com", "outlook.com", "hotmail.com", "icloud.com"];

const LoginSchema = z.object({
  email: z
    .string({ required_error: "login.email.error.required" })
    .min(1, "login.email.error.required")
    .email("login.email.error.invalid")
    .refine((v) => !BLOCKED_DOMAINS.includes(v.split("@")[1]?.toLowerCase() ?? ""), {
      message: "login.email.error.nonWork",
    }),
  // Password is intentionally not validated client-side — invalid/empty
  // values fall through to the action, which returns the generic
  // "Incorrect email or password" form-level error.
  password: z.string(),
});

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

export function meta({ matches }: Route.MetaArgs) {
  const brandName = getBrandName(matches);
  return [
    { title: i18n.t("login.meta.title", { brandName }) },
    { name: "description", content: i18n.t("login.meta.description", { brandName }) },
  ];
}

// ---------------------------------------------------------------------------
// Loader — reads ?sessionExpired search param
// ---------------------------------------------------------------------------

export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url);
  const sessionExpired = url.searchParams.get("sessionExpired") === "true";
  return { sessionExpired };
}

// ---------------------------------------------------------------------------
// Action
// ---------------------------------------------------------------------------

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();
  const submission = parseWithZod(formData, { schema: LoginSchema });

  if (submission.status !== "success") {
    return { lastResult: submission.reply() };
  }

  const { email, password } = submission.value;

  // When no API URL is configured (CI preview / local without backend) use
  // fixture-based responses so E2E tests remain green without a real backend.
  if (!env.apiBaseUrl) {
    if (email === AUTH_FIXTURES.emails.inactive) {
      return { lastResult: submission.reply({ fieldErrors: { email: ["login.error.inactive"] } }) };
    }
    if (email === AUTH_FIXTURES.emails.notFound || password !== AUTH_FIXTURES.passwords.correct) {
      return { lastResult: submission.reply({ formErrors: ["login.error.invalidCredentials"] }) };
    }
    // lookupNoBrands mirrors BLK-07: existing-partner lookup user with no
    // api_connections row — JWT has `connections: []`, so approvalStatus
    // collapses to "pending" and brandIDs is empty.
    const isLookupNoBrands = email === AUTH_FIXTURES.emails.lookupNoBrands;
    const approvalStatus =
      email === AUTH_FIXTURES.emails.pending || isLookupNoBrands ? "pending" : "approved";
    const fixtureBrandIDs = isLookupNoBrands ? null : undefined;
    const headers = new Headers();
    setSessionHeaders(
      headers,
      makeFixtureJWT(approvalStatus, email, fixtureBrandIDs),
      "fixture-refresh",
      900,
    );
    return redirect(
      approvalStatus === "pending" ? "/app/dashboard/pending" : "/app/dashboard/active",
      { headers },
    );
  }

  let resp: Response;
  try {
    resp = await fetch(`${env.apiBaseUrl}/api/v1/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    // API unreachable (preview / CI with no running backend) — fall back to fixtures.
    if (email === AUTH_FIXTURES.emails.inactive) {
      return { lastResult: submission.reply({ fieldErrors: { email: ["login.error.inactive"] } }) };
    }
    if (email === AUTH_FIXTURES.emails.notFound || password !== AUTH_FIXTURES.passwords.correct) {
      return { lastResult: submission.reply({ formErrors: ["login.error.invalidCredentials"] }) };
    }
    // lookupNoBrands mirrors BLK-07: existing-partner lookup user with no
    // api_connections row — JWT has `connections: []`, so approvalStatus
    // collapses to "pending" and brandIDs is empty.
    const isLookupNoBrands = email === AUTH_FIXTURES.emails.lookupNoBrands;
    const approvalStatus =
      email === AUTH_FIXTURES.emails.pending || isLookupNoBrands ? "pending" : "approved";
    const fixtureBrandIDs = isLookupNoBrands ? null : undefined;
    const headers = new Headers();
    setSessionHeaders(
      headers,
      makeFixtureJWT(approvalStatus, email, fixtureBrandIDs),
      "fixture-refresh",
      900,
    );
    return redirect(
      approvalStatus === "pending" ? "/app/dashboard/pending" : "/app/dashboard/active",
      { headers },
    );
  }

  if (!resp.ok) {
    const body = (await resp.json().catch(() => ({}))) as { error?: string };
    switch (body.error) {
      case "ACCOUNT_LOCKED":
        return {
          lastResult: submission.reply({
            formErrors: ["login.error.locked"],
          }),
        };
      case "ACCOUNT_INACTIVE":
        return {
          lastResult: submission.reply({
            fieldErrors: { email: ["login.error.inactive"] },
          }),
        };
      case "INVALID_CREDENTIALS":
      default:
        return {
          lastResult: submission.reply({
            formErrors: ["login.error.invalidCredentials"],
          }),
        };
    }
  }

  const data = (await resp.json()) as {
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };

  const payload = decodeJWT(data.access_token);
  type ConnClaim = { approval_status: string };
  const conns = Array.isArray(payload["connections"]) ? (payload["connections"] as ConnClaim[]) : [];
  const approvalStatus = conns.some((c) => c.approval_status === "approved") ? "approved" : "pending";
  const dest = approvalStatus === "pending" ? "/app/dashboard/pending" : "/app/dashboard/active";

  const headers = new Headers();
  setSessionHeaders(headers, data.access_token, data.refresh_token, data.expires_in);
  return redirect(dest, { headers });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function LoginPage() {
  const { t } = useTranslation();
  const { sessionExpired } = useLoaderData<typeof loader>();
  const rootData = useRouteLoaderData<typeof rootLoader>("root");
  const brand = rootData?.brand ?? "foodpanda";
  const brandName = BRAND_DISPLAY_NAMES[brand];
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  const [showPassword, setShowPassword] = useState(false);

  const [form, fields] = useForm({
    lastResult: actionData?.lastResult,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: LoginSchema });
    },
    shouldValidate: "onBlur",
    shouldRevalidate: "onInput",
  });

  return (
    <main className={`${styles.container} ${styles.containerMobile}`}>
      <Box className={styles.card}>
        {/* Header */}
        <Box className={styles.header}>
          <Box className={styles.illustration} aria-hidden="true">
            <LoginSvg className={styles.illustrationImg} />
          </Box>
          <Heading level={1} className={styles.title}>
            {t("login.heading", { brandName })}
          </Heading>
          <Box as="p" className={styles.subtitle}>
            {t("login.subtitle")}
          </Box>
        </Box>

        {/* Session-expired info banner */}
        {sessionExpired && (
          <Alert variant="info" style={{ marginBlockEnd: "var(--cp-spacing-large)" }}>
            {t("login.error.sessionExpired")}
          </Alert>
        )}

        {/* Form-level error banner (locked account, network failure) */}
        {form.errors != null && form.errors.length > 0 && (
          <Alert variant="error" style={{ marginBlockEnd: "var(--cp-spacing-large)" }}>
            {form.errors.map((key) => t(key)).join(" ")}
          </Alert>
        )}

        {/* Login form */}
        <Form method="post" {...getFormProps(form)}>
          <Stack direction="column" spacing="--cp-spacing-2xlarge">
            <Stack direction="column" spacing="--cp-spacing-medium">
              {/* Email */}
              <Box>
                <Label htmlFor={fields.email.id} required>
                  {t("login.email.label")}
                </Label>
                <TextInput
                  {...(fields.email.errors != null && fields.email.errors.length > 0
                    ? { validationState: "error" as const }
                    : {})}
                >
                  <TextInputControl
                    {...getInputProps(fields.email, { type: "email" })}
                    placeholder={t("login.email.placeholder")}
                    autoComplete="email"
                    disabled={isSubmitting}
                  />
                </TextInput>
                {/* Always rendered to reserve vertical space (prevents layout shift) */}
                <HelperText
                  id={fields.email.errorId}
                  variant="error"
                  role="alert"
                  style={{
                    visibility:
                      fields.email.errors != null && fields.email.errors.length > 0
                        ? "visible"
                        : "hidden",
                  }}
                >
                  {fields.email.errors != null && fields.email.errors.length > 0
                    ? t(fields.email.errors[0] ?? "")
                    : "\u00A0"}
                </HelperText>
              </Box>

              {/* Password */}
              <Box>
                <Box className={styles.passwordLabelRow}>
                  <Label htmlFor={fields.password.id} required>
                    {t("login.password.label")}
                  </Label>
                  <Link to="/auth/forgot-password" className={styles.forgotLink}>
                    {t("login.cta.forgotPassword")}
                  </Link>
                </Box>
                <TextInput>
                  <TextInputControl
                    {...getInputProps(fields.password, {
                      type: showPassword ? "text" : "password",
                    })}
                    placeholder={t("login.password.placeholder")}
                    autoComplete="current-password"
                    disabled={isSubmitting}
                  />
                  <TextInputSlot>
                    <button
                      type="button"
                      aria-label={
                        showPassword ? t("login.password.hide") : t("login.password.show")
                      }
                      onClick={() => setShowPassword((v) => !v)}
                      disabled={isSubmitting}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "var(--cp-action-spacing-xsmall)",
                        display: "flex",
                        alignItems: "center",
                        color: "var(--cp-color-jacaranda-grey-50)",
                        width: "var(--cp-icon-sizing-medium)",
                        height: "var(--cp-icon-sizing-medium)",
                      }}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon aria-hidden="true" />
                      ) : (
                        <VisibilityOnIcon aria-hidden="true" />
                      )}
                    </button>
                  </TextInputSlot>
                </TextInput>
              </Box>
            </Stack>

            {/* Primary CTA */}
            <Button
              type="submit"
              variant="primary"
              size="small"
              className={styles.fullWidth}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? t("login.submitting") : t("login.submit")}
            </Button>

            {/* Secondary CTAs */}
            <Stack direction="column" spacing="--cp-spacing-medium">
              <Link to="/auth/register" className={styles.link}>
                <Button
                  variant="secondary"
                  size="small"
                  className={styles.fullWidth}
                  disabled={isSubmitting}
                >
                  {t("login.cta.register")}
                </Button>
              </Link>

              <Link to="/auth/existing-partner" className={styles.link}>
                <Button
                  variant="tertiary"
                  size="small"
                  className={styles.fullWidth}
                  disabled={isSubmitting}
                >
                  {t("login.cta.existingPartner")}
                </Button>
              </Link>
            </Stack>
          </Stack>
        </Form>
      </Box>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Error boundary — required on every route file (developer conventions)
// ---------------------------------------------------------------------------

export function ErrorBoundary() {
  const { t } = useTranslation();
  return (
    <main className={`${styles.container} ${styles.containerMobile}`}>
      <Box className={styles.card}>
        <Alert variant="error">{t("error.default.message")}</Alert>
      </Box>
    </main>
  );
}
